import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  User,
  CreditCard,
  Loader,
  Lock,
  MailCheck,
  Zap,
} from 'lucide-react';
import { companyInfo } from '../mock';
import Footer from '../components/Footer';
import { auth, db, storage } from '../lib/firebase';
import {
  updateProfile,
  sendEmailVerification,
  updatePassword,
} from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { toast } from 'sonner';
import { useUser } from '../context/UserContext';
import {
  cancelSubscription,
  initializeRazorpaySubscription,
} from '../lib/razorpayHandler';
import ReAuthModal from '../components/ReAuthModal';

const AccountSettings = () => {
  const [activeTab, setActiveTab] = useState('profile');
  const [displayName, setDisplayName] = useState(
    auth.currentUser?.displayName || ''
  );
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(
    auth.currentUser?.photoURL || null
  );
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifyingEmail, setVerifyingEmail] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [showReAuthModal, setShowReAuthModal] = useState(false);

  // subscription-only
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);

  const { plan, status, loading: userLoading, refreshUser } = useUser();
  const navigate = useNavigate();
  const user = auth.currentUser;

  /* ---------------- Guards ---------------- */

  if (userLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg-dark">
        <Loader className="animate-spin text-accent-teal w-8 h-8" />
      </div>
    );
  }

  if (!user) {
    navigate('/tools/auth?redirect=/account-settings');
    return null;
  }

  /* ---------------- Profile Logic ---------------- */

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be under 5MB');
      return;
    }

    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleUpdateProfile = async () => {
    setLoading(true);
    try {
      let photoURL = user.photoURL;

      if (photoFile) {
        const storageRef = ref(
          storage,
          `profile-photos/${user.uid}/${Date.now()}_${photoFile.name}`
        );
        await uploadBytes(storageRef, photoFile);
        photoURL = await getDownloadURL(storageRef);
      }

      await updateProfile(user, { displayName, photoURL });

      await setDoc(
        doc(db, 'users', user.uid),
        {
          displayName,
          photoURL,
          email: user.email,
          updatedAt: new Date().toISOString(),
        },
        { merge: true }
      );

      toast.success('Profile updated successfully');
      setPhotoFile(null);
    } catch (err) {
      console.error(err);
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSendVerification = async () => {
    if (user.emailVerified) return;

    setVerifyingEmail(true);
    try {
      await sendEmailVerification(user);
      toast.success('Verification email sent. Check your inbox.');
    } catch {
      toast.error('Failed to send verification email');
    } finally {
      setVerifyingEmail(false);
    }
  };

  const performPasswordUpdate = async () => {
    setChangingPassword(true);
    try {
      await updatePassword(user, newPassword);
      toast.success('Password updated successfully');
      setNewPassword('');
    } catch (err) {
      if (err.code === 'auth/requires-recent-login') {
        setShowReAuthModal(true);
      } else {
        toast.error('Failed to update password');
      }
    } finally {
      setChangingPassword(false);
    }
  };

  const handleChangePassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    await performPasswordUpdate();
  };

  /* ---------------- Subscription Logic ---------------- */

  const handleCancelSubscription = async () => {
    if (plan === 'free') return;
    if (!window.confirm('Are you sure you want to cancel your subscription?'))
      return;

    try {
      await cancelSubscription();
      toast.success('Subscription cancelled');
    } catch (e) {
      toast.error(e.message || 'Failed to cancel subscription');
    }
  };

  const handleUpgrade = async (tier) => {
    if (!user) return;

    setProcessingPayment(true);

    await initializeRazorpaySubscription(
      tier,
      user,
      async () => {
        toast.success('Payment successful! Activating subscription…');
        setShowUpgrade(false);
        setTimeout(refreshUser, 1500);
      },
      (err) => {
        toast.error(err?.message || 'Payment failed');
      }
    );

    setProcessingPayment(false);
  };

  const getPlanDetails = (plan) => {
    const plans = {
      free: {
        name: 'Free',
        price: '$0 / month',
        description: 'Explore NT Studios AI tools',
        features: ['All AI tools included', '10 basic generations per day'],
      },
      plus: {
        name: 'Plus',
        price: '$19 / month',
        description: 'For solo founders & creators',
        features: [
          'All AI tools included',
          '50 basic generations per day',
          '15 deep research generations per month',
        ],
      },
      pro: {
        name: 'Pro',
        price: '$39 / month',
        description: 'For agencies & power users',
        features: [
          'All AI tools included',
          '100 basic generations per day',
          '50 deep research generations per month',
        ],
      },
    };
    return plans[plan] || plans.free;
  };

  const planDetails = getPlanDetails(plan);

  /* ---------------- UI ---------------- */

  return (
    <div className="min-h-screen bg-bg-dark">
      {showReAuthModal && (
        <ReAuthModal
          onClose={() => setShowReAuthModal(false)}
          onSuccess={performPasswordUpdate}
        />
      )}

      <nav className="border-b border-white/10 bg-neutral-charcoal">
        <div className="container mx-auto px-6 py-4">
          <Link to="/" className="flex items-center gap-3">
            <img src={companyInfo.logo} alt="NT Studios" className="h-14" />
          </Link>
        </div>
      </nav>

      <main className="container mx-auto px-6 py-12 max-w-5xl">
        <Link to="/" className="inline-flex items-center gap-2 text-accent-teal mb-8">
          <ArrowLeft size={18} />
          Back to Home
        </Link>

        <h1 className="text-4xl font-bold text-white mb-10">
          Account Settings
        </h1>

        <div className="flex gap-8 border-b border-white/10 mb-10">
          <button
            onClick={() => setActiveTab('profile')}
            className={`flex items-center gap-2 pb-3 font-medium ${
              activeTab === 'profile'
                ? 'text-accent-teal border-b-2 border-accent-teal'
                : 'text-neutral-silver hover:text-white'
            }`}
          >
            <User size={18} />
            Profile
          </button>

          <button
            onClick={() => setActiveTab('subscription')}
            className={`flex items-center gap-2 pb-3 font-medium ${
              activeTab === 'subscription'
                ? 'text-accent-teal border-b-2 border-accent-teal'
                : 'text-neutral-silver hover:text-white'
            }`}
          >
            <CreditCard size={18} />
            Subscription
          </button>
        </div>

        {/* PROFILE TAB — ACTUALLY RESTORED */}
        {activeTab === 'profile' && (
          <div className="bg-neutral-charcoal border border-white/10 rounded-xl p-8">
            <h2 className="text-2xl font-bold text-white mb-8">
              Profile Settings
            </h2>

            <div className="flex items-center gap-6 mb-8">
              <div className="w-24 h-24 rounded-full bg-accent-teal/20 border-2 border-accent-teal flex items-center justify-center overflow-hidden">
                {photoPreview ? (
                  <img
                    src={photoPreview}
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <User size={40} className="text-accent-teal" />
                )}
              </div>

              <div>
                <label className="block text-white mb-2">
                  Profile Photo
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoChange}
                />
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-white mb-2">
                Display Name
              </label>
              <input
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-white/10"
                style={{
                  backgroundColor: '#2a2b2d',
                  color: '#fff',
                  WebkitTextFillColor: '#fff',
                }}
              />
            </div>

            <div className="mb-6">
              <label className="block text-white mb-2">Email</label>
              <input
                value={user.email}
                disabled
                className="w-full px-4 py-3 bg-neutral-charcoal border border-white/10 rounded-lg text-neutral-silver"
              />

              {user.emailVerified ? (
                <p className="mt-2 text-green-400 flex items-center gap-2">
                  <MailCheck size={16} /> Email verified
                </p>
              ) : (
                <button
                  onClick={handleSendVerification}
                  disabled={verifyingEmail}
                  className="mt-2 text-sm text-accent-teal hover:underline disabled:opacity-50"
                >
                  {verifyingEmail
                    ? 'Sending verification…'
                    : 'Verify email'}
                </button>
              )}
            </div>

            <div className="mb-8">
              <label className="block text-white mb-2">
                Change Password
              </label>
              <input
                type="password"
                placeholder="New password (min 6 characters)"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-white/10 mb-3"
                style={{
                  backgroundColor: '#2a2b2d',
                  color: '#fff',
                  WebkitTextFillColor: '#fff',
                }}
              />
              <button
                onClick={handleChangePassword}
                disabled={changingPassword}
                className="flex items-center gap-2 px-4 py-3 bg-neutral-charcoal border border-white/10 rounded-lg text-white"
              >
                <Lock size={16} />
                {changingPassword ? 'Updating…' : 'Update Password'}
              </button>
            </div>

            <button
              onClick={handleUpdateProfile}
              disabled={loading}
              className="px-8 py-3 bg-accent-teal text-white rounded-lg font-semibold disabled:opacity-50"
            >
              {loading ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        )}

        {/* SUBSCRIPTION TAB */}
        {activeTab === 'subscription' && (
          <div className="bg-neutral-charcoal border border-white/10 rounded-xl p-8">
            <h2 className="text-2xl font-bold text-white mb-6">
              Current Subscription
            </h2>

            <h3 className="text-3xl font-bold text-accent-teal">
              {planDetails.name}
            </h3>
            <p className="text-white mb-2">{planDetails.price}</p>
            <p className="text-neutral-silver mb-6">
              {planDetails.description}
            </p>

            <ul className="space-y-2 mb-6">
              {planDetails.features.map((f, i) => (
                <li key={i} className="text-neutral-silver">
                  • {f}
                </li>
              ))}
            </ul>

            <div className="flex flex-wrap gap-4 mb-6">
              {plan !== 'pro' && (
                <button
                  onClick={() => setShowUpgrade(!showUpgrade)}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-accent-teal text-white rounded-lg font-semibold"
                >
                  <Zap size={18} />
                  Upgrade / Change Plan
                </button>
              )}

              {plan !== 'free' && status === 'active' && (
                <button
                  onClick={handleCancelSubscription}
                  className="px-6 py-3 bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg"
                >
                  Cancel Subscription
                </button>
              )}
            </div>

            {showUpgrade && (
              <div className="grid md:grid-cols-2 gap-8 mt-6">
                {/* PLUS */}
                <div className="rounded-xl p-6 bg-white/5 border border-white/10 text-center">
                  <h3 className="text-2xl font-bold text-white mb-2">
                    PLUS
                  </h3>
                  <p className="text-accent-teal text-3xl font-bold mb-4">
                    $19/month
                  </p>
                  <ul className="text-neutral-silver text-sm space-y-1 mb-6">
                    {getPlanDetails('plus').features.map((f, i) => (
                      <li key={i}>• {f}</li>
                    ))}
                  </ul>
                  <button
                    disabled={processingPayment}
                    onClick={() => handleUpgrade('plus')}
                    className="w-full py-3 bg-accent-teal text-black rounded-lg font-semibold"
                  >
                    Upgrade to PLUS
                  </button>
                </div>

                {/* PRO */}
                <div className="rounded-xl p-6 bg-white/5 border border-accent-teal/40 text-center">
                  <h3 className="text-2xl font-bold text-white mb-2">
                    PRO
                  </h3>
                  <p className="text-accent-teal text-3xl font-bold mb-4">
                    $39/month
                  </p>
                  <ul className="text-neutral-silver text-sm space-y-1 mb-6">
                    {getPlanDetails('pro').features.map((f, i) => (
                      <li key={i}>• {f}</li>
                    ))}
                  </ul>
                  <button
                    disabled={processingPayment}
                    onClick={() => handleUpgrade('pro')}
                    className="w-full py-3 bg-accent-teal text-black rounded-lg font-semibold"
                  >
                    Upgrade to PRO
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default AccountSettings;
